import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useBillingOverview } from "@/hooks/useBillingOverview";
import type { BillingOverview } from "@/lib/billing-overview";
import {
  bogotaDay,
  hasActiveAutoDebit,
  resolveAutoDebitDueDay,
  type AutoDebitDueDay,
  type AutoDebitPreapproval,
  type AutoDebitRejection,
} from "@/lib/auto-debit-due-day";

export interface UseAutoDebitDueDayArgs {
  orgId: string | null;
  preapproval: AutoDebitPreapproval | null;
  paymentMode: string | null;
  nextBillingDate: string | null;
  fallbackAmount: number;
  overview?: BillingOverview | null;
  overviewLoading?: boolean;
}

export interface AutoDebitDueDayState {
  autoDebit: AutoDebitDueDay | null;
  active: boolean;
  loading: boolean;
}

export function useAutoDebitDueDay({
  orgId,
  preapproval,
  paymentMode,
  nextBillingDate,
  fallbackAmount,
  overview,
  overviewLoading = false,
}: UseAutoDebitDueDayArgs): AutoDebitDueDayState {
  const active = Boolean(orgId) && hasActiveAutoDebit(preapproval, paymentMode);
  const fetchOverview = active && overview === undefined;
  const fetched = useBillingOverview(fetchOverview ? orgId : null);
  const resolvedOverview = overview === undefined ? fetched.overview : overview;
  const resolvedOverviewLoading = overview === undefined ? fetchOverview && fetched.loading : overviewLoading;

  const [loaded, setLoaded] = useState<{ orgId: string; rejection: AutoDebitRejection | null } | null>(null);

  useEffect(() => {
    if (!active || !orgId) return;
    let cancelled = false;
    supabase
      .from("subscription_payments")
      .select("failure_reason, paid_at, created_at")
      .eq("organization_id", orgId)
      .eq("status", "rejected")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error("[useAutoDebitDueDay]", error.message);
        const at = data?.paid_at ?? data?.created_at ?? null;
        setLoaded({ orgId, rejection: at ? { at, reason: data?.failure_reason ?? null } : null });
      });
    return () => { cancelled = true; };
  }, [active, orgId]);

  const rejectionReady = loaded !== null && loaded.orgId === orgId;
  const rejection = rejectionReady ? loaded.rejection : null;
  const loading = active && (resolvedOverviewLoading || !rejectionReady);

  const autoDebit = useMemo(() => {
    if (!active || loading) return null;
    return resolveAutoDebitDueDay({
      preapproval,
      paymentMode,
      today: resolvedOverview?.today || bogotaDay(new Date()) || "",
      owedCycles: resolvedOverview?.owedCycles ?? [],
      owedTotal: resolvedOverview?.owedTotal ?? 0,
      nextBillingDate,
      fallbackAmount,
      latestRejection: rejection,
    });
  }, [active, loading, preapproval, paymentMode, resolvedOverview, nextBillingDate, fallbackAmount, rejection]);

  return { autoDebit, active, loading };
}
